import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { AlertTriangle, ShieldCheck, RefreshCw, Send, X } from "lucide-react";

/**
 * Escalation Board — real-time dashboard of every HIGH-risk shared-mailbox
 * thread stamped by Claude. Owner/admin only.
 *
 * Auto-refreshes every 60s. Groups threads by primary risk-flag bucket
 * (escalation / sentiment / payment / deadline / delivery / signal / other).
 */
export default function Escalations() {
  const { activeWorkspaceId } = useAuth();
  const [buckets, setBuckets] = useState([]);
  const [total, setTotal] = useState(0);
  const [windowHours, setWindowHours] = useState(72);
  const [includeHandled, setIncludeHandled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [replying, setReplying] = useState(null); // thread object
  // R148n · Feb 2026 — Manual escalation logging
  const [manualRows, setManualRows] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({ title: "", description: "", severity: "medium", source: "" });
  const [logBusy, setLogBusy] = useState(false);

  const loadManual = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/escalations/manual`);
      setManualRows(Array.isArray(data) ? data : []);
    } catch { /* silent — non-blocking secondary section */ }
  };

  const submitLog = async () => {
    if (!logForm.title.trim()) { toast.info("Please add a title"); return; }
    setLogBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/escalations/manual`, {
        ...logForm,
        title: logForm.title.trim(),
        description: (logForm.description || "").trim() || null,
        source: (logForm.source || "").trim() || null,
      });
      toast.success("Escalation logged");
      setLogOpen(false);
      setLogForm({ title: "", description: "", severity: "medium", source: "" });
      loadManual();
    } catch (e) { toast.error(apiErrorMessage(e) || "Log failed"); }
    finally { setLogBusy(false); }
  };

  const patchManual = async (id, patch) => {
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/escalations/manual/${id}`, patch);
      loadManual();
    } catch (e) { toast.error(apiErrorMessage(e) || "Update failed"); }
  };

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/escalations`,
        { params: { window_hours: windowHours, include_handled: includeHandled } });
      setBuckets(data.buckets || []); setTotal(data.total || 0);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load escalations"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); loadManual(); /* eslint-disable-next-line */ }, [activeWorkspaceId, windowHours, includeHandled]);
  useEffect(() => {
    // Poll every 60s as a fallback if WS drops
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [activeWorkspaceId, windowHours, includeHandled]);

  // Live push — new HIGH-risk threads show up instantly
  const wsRef = useRef(null);
  const [newSinceLastLoad, setNewSinceLastLoad] = useState(0);
  useEffect(() => {
    if (!activeWorkspaceId) return () => {};
    const token = localStorage.getItem("co_token");
    if (!token) return () => {};
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const wsUrl = `${base}/api/v1/ws?token=${encodeURIComponent(token)}&workspace_id=${activeWorkspaceId}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "escalation:new" && msg.workspace_id === activeWorkspaceId) {
            setNewSinceLastLoad((n) => n + 1);
            // Also toast so it's impossible to miss
            toast.warning(`⚠️ New HIGH-risk thread: ${msg.subject}`, {
              action: { label: "Refresh", onClick: () => { setNewSinceLastLoad(0); load(); } },
              duration: 10000,
            });
          }
        } catch { /* ignore */ }
      };
    } catch { /* fallback to polling */ }
    return () => { if (ws) try { ws.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line
  }, [activeWorkspaceId]);

  const dismiss = async (t) => {
    if (!window.confirm(`Mark "${t.subject}" as handled?`)) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/escalations/${t.id}/dismiss`);
      toast.success("Marked handled");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Dismiss failed"); }
  };

  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 max-w-6xl space-y-4" data-testid="escalations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h1 className="text-2xl font-bold">Escalation Board</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Every shared-inbox thread Claude flagged as HIGH-risk in the last {windowHours}h. Grouped by risk-flag. Auto-refreshes every 60s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={windowHours} onChange={(e) => setWindowHours(Number(e.target.value))}
                  className="h-9 border border-border bg-background px-2 text-sm" data-testid="esc-window">
            <option value={24}>Last 24h</option>
            <option value={72}>Last 72h</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <label className="text-xs inline-flex items-center gap-1.5">
            <input type="checkbox" checked={includeHandled} onChange={(e) => setIncludeHandled(e.target.checked)} data-testid="esc-include-handled" />
            Show handled
          </label>
          <Button size="sm" variant="outline" className="rounded-none h-9" onClick={load} disabled={busy} data-testid="esc-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" className="rounded-none h-9" onClick={() => setLogOpen(true)} data-testid="esc-log-btn">
            <AlertTriangle className="w-4 h-4 mr-1" /> Log escalation
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="High-risk threads" value={total} highlight />
        <StatCard label="Buckets" value={buckets.length} />
        <StatCard label="Handled" value={buckets.reduce((a, b) => a + b.threads.filter((t) => t.escalation_handled).length, 0)} />
        <StatCard label="Auto-refresh" value="60s + live" />
      </div>

      {newSinceLastLoad > 0 && (
        <div className="border-l-4 border-red-500 bg-red-50 text-red-900 px-4 py-3 flex items-center justify-between"
             data-testid="esc-new-since">
          <span className="text-sm font-semibold">
            🚨 {newSinceLastLoad} new HIGH-risk thread{newSinceLastLoad === 1 ? "" : "s"} arrived. Refresh to see.
          </span>
          <Button size="sm" className="rounded-none h-8" onClick={() => { setNewSinceLastLoad(0); load(); }}
                  data-testid="esc-load-new">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Load {newSinceLastLoad}
          </Button>
        </div>
      )}

      {busy && buckets.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6">Loading…</div>
      ) : total === 0 ? (
        <div className="border border-dashed border-emerald-300 bg-emerald-50 p-10 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
          <div className="text-emerald-800 font-semibold">All clear — no HIGH-risk shared-inbox threads in this window.</div>
          <p className="text-sm text-emerald-700 mt-1">
            The AI classifier is watching every reply. You&apos;ll see them here the moment one escalates.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {buckets.map((b) => (
            <div key={b.key} className="border border-border" data-testid={`esc-bucket-${b.key}`}>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between"
                   style={{ borderLeft: `4px solid ${b.color}` }}>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{b.label}</span>
                  <span className="text-[10px] uppercase tracking-widest border border-border bg-muted/40 px-1.5 py-0.5">
                    {b.threads.length} thread{b.threads.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-border">
                {b.threads.map((t) => (
                  <div key={t.id} className={`p-4 ${t.escalation_handled ? "opacity-60" : ""}`} data-testid={`esc-thread-${t.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{t.subject || "(no subject)"}</span>
                          {t.escalation_handled && (
                            <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5">
                              Handled
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-widest border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5">
                            {t.ai_sentiment || "high-risk"}
                          </span>
                          {(t.ai_risk_flags || []).slice(0, 3).map((f) => (
                            <span key={f} className="text-[10px] uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5">
                              {f.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {(t.participants || []).join(", ")} · {t.message_count} msg · {new Date(t.last_activity_at).toLocaleString()}
                        </div>
                        {t.ai_summary && (
                          <p className="text-sm mt-2 text-foreground/90">{t.ai_summary}</p>
                        )}
                        {t.ai_next_action && (
                          <p className="text-xs mt-1 text-primary">
                            <strong>Next action:</strong> {t.ai_next_action}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {!t.escalation_handled && t.ai_suggested_reply && (
                          <Button size="sm" className="rounded-none h-8" onClick={() => setReplying(t)} data-testid={`esc-reply-${t.id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" /> Reply with draft
                          </Button>
                        )}
                        {!t.escalation_handled && (
                          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => dismiss(t)} data-testid={`esc-dismiss-${t.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" /> Dismiss
                          </Button>
                        )}
                        <a href={`/app/inbox?thread=${t.id}`} className="text-xs underline text-primary self-center">
                          Open thread →
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* R148n · Feb 2026 — Manually logged escalations */}
      <div className="border border-border" data-testid="esc-manual-section">
        <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest font-semibold">
            Manually logged escalations · {manualRows.length}
          </div>
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setLogOpen(true)} data-testid="esc-log-btn-2">
            <AlertTriangle className="w-3.5 h-3.5 mr-1" /> New
          </Button>
        </div>
        {manualRows.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground text-center">
            Nothing logged manually yet. Use <b>Log escalation</b> above to capture a Slack thread, client call or in-person report.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {manualRows.map((m) => {
              const sev = m.severity || "medium";
              const sevTone = sev === "critical" ? "border-red-600 bg-red-50 text-red-800"
                : sev === "high" ? "border-orange-500 bg-orange-50 text-orange-800"
                : sev === "low" ? "border-slate-400 bg-slate-100 text-slate-700"
                : "border-amber-500 bg-amber-50 text-amber-800";
              const done = m.status === "resolved" || m.status === "closed";
              return (
                <li key={m.id} className={`p-3 flex items-start gap-3 ${done ? "opacity-60" : ""}`} data-testid={`esc-manual-${m.id}`}>
                  <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 mt-0.5 ${sevTone}`}>{sev}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{m.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {m.client_name && <span>Client: {m.client_name} · </span>}
                      {m.brand_name && <span>Brand: {m.brand_name} · </span>}
                      {m.project_name && <span>Project: {m.project_name} · </span>}
                      {m.source && <span>Source: {m.source} · </span>}
                      Logged by {m.created_by_name || "—"}
                    </div>
                    {m.description && <div className="text-xs mt-1 whitespace-pre-wrap">{m.description}</div>}
                    {m.resolution_note && (
                      <div className="text-xs mt-1 border-l-2 border-emerald-500 pl-2 text-emerald-800 whitespace-pre-wrap">
                        <b>Resolution:</b> {m.resolution_note}
                      </div>
                    )}
                  </div>
                  <select
                    value={m.status || "open"}
                    onChange={(e) => patchManual(m.id, { status: e.target.value })}
                    className="h-7 text-[10px] border border-border bg-background px-1"
                    data-testid={`esc-manual-status-${m.id}`}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {replying && (
        <QuickReplyDialog thread={replying} wsId={activeWorkspaceId}
                            onClose={() => setReplying(null)}
                            onSent={() => { setReplying(null); load(); }} />
      )}

      {logOpen && (
        <Dialog open onOpenChange={(v) => !v && setLogOpen(false)}>
          <DialogContent className="max-w-lg rounded-none" data-testid="esc-log-dialog">
            <DialogHeader><DialogTitle>Log an escalation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Title *</label>
                <input
                  type="text"
                  value={logForm.title}
                  onChange={(e) => setLogForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full h-9 border border-border bg-background px-2 text-sm mt-0.5"
                  placeholder="e.g. Client threatening to churn if PR isn't merged today"
                  data-testid="esc-log-title"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Severity</label>
                  <select value={logForm.severity} onChange={(e) => setLogForm((f) => ({ ...f, severity: e.target.value }))}
                          className="w-full h-9 border border-border bg-background px-2 text-sm mt-0.5" data-testid="esc-log-severity">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Source</label>
                  <input
                    type="text"
                    value={logForm.source}
                    onChange={(e) => setLogForm((f) => ({ ...f, source: e.target.value }))}
                    className="w-full h-9 border border-border bg-background px-2 text-sm mt-0.5"
                    placeholder="Slack / Client call / Ticket #123"
                    data-testid="esc-log-source"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
                <Textarea
                  rows={5}
                  value={logForm.description}
                  onChange={(e) => setLogForm((f) => ({ ...f, description: e.target.value }))}
                  className="rounded-none mt-0.5"
                  placeholder="What happened, who's affected, next step you have in mind…"
                  data-testid="esc-log-description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setLogOpen(false)} disabled={logBusy}>Cancel</Button>
              <Button onClick={submitLog} disabled={logBusy || !logForm.title.trim()} data-testid="esc-log-submit">
                {logBusy ? "Saving…" : "Log escalation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`border p-4 ${highlight ? "bg-foreground text-background border-foreground" : "border-border"}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function QuickReplyDialog({ thread, wsId, onClose, onSent }) {
  const [html, setHtml] = useState(thread.ai_suggested_reply || "");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!html.trim()) { toast.error("Reply cannot be empty"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/escalations/${thread.id}/quick-reply`,
        { override_html: html !== thread.ai_suggested_reply ? html : undefined });
      toast.success("Reply sent from shared mailbox");
      onSent && onSent();
    } catch (e) { toast.error(apiErrorMessage(e) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="quick-reply-dialog"
                     description={`Send Claude's suggested draft (editable) as a reply to this escalation via the shared workspace mailbox.`}>
        <DialogHeader><DialogTitle>Quick reply — {thread.subject}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground border-b border-border pb-2">
            Sending from your workspace <strong>shared mailbox</strong>. This is Claude&apos;s suggested draft — edit before sending or ship as-is.
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Draft (HTML, editable)</label>
            <Textarea rows={14} value={html} onChange={(e) => setHtml(e.target.value)}
                      className="font-mono text-xs rounded-none" data-testid="quick-reply-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={busy || !html.trim()} data-testid="quick-reply-send">
            <Send className="w-4 h-4 mr-1" /> {busy ? "Sending…" : "Send reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
